import { APP_VERSION, EVENTS_PREFIX } from "core/config";
import { ITCFConsentState, TCFEventStatus } from "./TCFConsentProvider/types";
import TCFGenerateTCString from "./tcfHelpers/TCFGenerateTCString";
import { TCF_AC_STRING_VERSION, TCF_CMP_ID, TCF_POLICY_VERSION } from "./constantsTCF";
import convertSemanticVersion from "./tcfHelpers/TCFConvertSemanticVersion";
import getTCFEventStatus from "./tcfHelpers/getTCFEventStatus";
import TCFGenerateACString from "./tcfHelpers/TCFGenerateACString";
import { FALSE, TRUE } from "constants/primitives";
import { ITCFGlobalVendorList as ITCFGlobalVendorListResponse } from "types/tcf";
import WidgetConfig from "helpers/init/WidgetConfig";
import { NUMBER_0 } from "constants/numbers";

export const getTcData = (listenerID: number | string, consent: ITCFConsentState, widgetConfig: WidgetConfig, gvl: ITCFGlobalVendorListResponse | undefined, userLang: string): Promise<ITcDataReturn> => {
    const isLoaded = window.dataLayer && window.dataLayer.findIndex(x => x.event === `${EVENTS_PREFIX}_init`) >= NUMBER_0;

    return new Promise((resolve, reject) => {
        try {
            // Convert listenerID to number
            const convListenerID = Number(listenerID);

            const TcData: ITcDataReturn = {
                tcString: TCFGenerateTCString(gvl, consent, userLang),
                tcfPolicyVersion: TCF_POLICY_VERSION,
                cmpId: TCF_CMP_ID,
                cmpVersion: convertSemanticVersion(APP_VERSION),

                /**
                 * true - GDPR Applies
                 * false - GDPR Does not apply
                 * undefined - unknown whether GDPR Applies
                 * see the section: "What does the gdprApplies value mean?"
                 */
                gdprApplies: true,

                /*
                 * see addEventListener command
                 */
                eventStatus: getTCFEventStatus(consent.eventStatus),

                /**
                 * see Ping Status Codes in following table
                 */
                // TODO: UPDATE ALL OF THE STATUSES
                cmpStatus: isLoaded ? 'loaded' : 'stub',

                /**
                 * If this TCData is sent to the callback of addEventListener: number,
                 * the unique ID assigned by the CMP to the listener function registered
                 * via addEventListener.
                 * Others: undefined.
                 */
                listenerId: convListenerID,

                /*
                 * true - Default value
                 * false - TC String is invalid.
                 * since Sept 1st 2021, TC strings established with global-scope are considered invalid.
                 * see the section: ["What happened to Global Scope and Out of Band?"](https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/TCF-Implementation-Guidelines.md#gsoob) in "IAB Europe Transparency and Consent Framework Implementation Guidelines"
                 */
                isServiceSpecific: TRUE,

                /**
                 * true - CMP is using publisher-customized stack descriptions and/or modified or supplemented standard Illustrations
                 * false - CMP is NOT using publisher-customized stack descriptions and or modified or supplemented standard Illustrations
                 */
                useNonStandardTexts: FALSE,

                /**
                 * Country code of the country that determines the legislation of
                 * reference.  Normally corresponds to the country code of the country
                 * in which the publisher's business entity is established.
                 */
                publisherCC: consent.tcfCountry,

                /**
                 *
                 * true - Purpose 1 not disclosed at all. CMPs use PublisherCC to
                 * indicate the publisher's country of establishment to help Vendors
                 * determine whether the vendor requires Purpose 1 consent.
                 *
                 * false - There is no special Purpose 1 treatment status. Purpose 1 was
                 * disclosed normally (consent) as expected by TCF Policy
                 */
                purposeOneTreatment: TRUE,

                purpose: {
                    consents: consent.userConsent.categories['purposes'] || {},
                    legitimateInterests: consent.userConsent.categoriesLegitimateInterest['purposes'] || {}
                },
                vendor: {
                    consents: consent.userConsent.vendors,
                    legitimateInterests: consent.userConsent.vendorsLegitimateInterest
                },
                specialFeatureOptins: consent.userConsent.categories['specialFeatures'] || {},
                publisher: {
                    consents: {},
                    legitimateInterests: {},
                    customPurpose: {
                        consents: {},
                        legitimateInterests: {},
                    },
                    restrictions: {}
                },
                addtlConsent: widgetConfig.acEnabled && consent.hasConsented ? TCFGenerateACString(consent.userConsent.gatp, widgetConfig.acVendors) : `${TCF_AC_STRING_VERSION}~`,
                enableAdvertiserConsentMode: widgetConfig.googleConsentModeEnabled
            };

            resolve(TcData);
        } catch (error) {
            reject(error);
        }
    });
}

export interface ITcDataReturn {
    tcString: string,
    tcfPolicyVersion: number,
    cmpId: number,
    cmpVersion: number,
    gdprApplies: boolean,
    eventStatus: TCFEventStatus,
    cmpStatus: string,
    listenerId: number,
    isServiceSpecific: boolean,
    useNonStandardTexts: boolean,
    publisherCC: string,
    purposeOneTreatment: boolean,
    purpose: {
        consents: {
            [key: string]: boolean,
        },
        legitimateInterests: {
            [key: string]: boolean,
        }
    },
    vendor: {
        consents: {
            [key: string]: boolean,
        }
        legitimateInterests: {
            [key: string]: boolean,
        }
    },
    specialFeatureOptins: {
        [key: string]: boolean,
    },
    publisher: {
        consents: {
            [key: string]: boolean,
        },
        legitimateInterests: {
            [key: string]: boolean,
        },
        customPurpose: {
            consents: {
                [key: string]: boolean,
            },
            legitimateInterests: {
                [key: string]: boolean,
            }
        },
        restrictions: {
            [key: string]: {
                [key: string]: number
            }
        }
    },
    addtlConsent: string | undefined,
    enableAdvertiserConsentMode: boolean,
}
